import {
    ClinicFieldsFragment,
    FilterOp,
    OrderByInput,
    PracticeManagementSystem,
    QueryInput,
} from '~graphql/generated/graphql'
import { getExisting } from '~utils/helpers'

type ClinicDataSource = Pick<
    ClinicFieldsFragment,
    'id' | 'pms' | 'pms_db'
> | null | undefined

type AppointmentClinicianRef = {
    id: string
    pms_id?: string | null
}

export function getClinicQueryInput(clinicId: string): QueryInput {
    return {
        column: 'clinic',
        filterOp: FilterOp.Eq,
        value: {
            string: clinicId,
        },
    }
}

export function getDataSourceQueryInput(
    clinic: Pick<ClinicFieldsFragment, 'id' | 'pms_db'> | null | undefined,
) {
    return clinic?.pms_db
        ? constructStringEq('pms_db', clinic.pms_db.id)
        : getClinicQueryInput(clinic?.id || '')
}

/**
 * QueryInput column the server lifts into a PostgREST `.or()` group.
 * Must stay in sync with `POSTGREST_OR_FILTER_COLUMN` in
 * `server/src/db/helpers/query.helpers.ts`.
 */
export const POSTGREST_OR_FILTER_COLUMN = '__or'

type ClinicDataSourceRef = {
    id: string
    pms_db?: { id: string } | null
}

/**
 * Scope for a PMS-synced entity search across one clinic or a clinic group.
 *
 * Shared-sync rows are keyed by `pms_db` (`clinic` is often null). Other rows
 * for the same clinics are keyed by `clinic` with a null `pms_db`. Matching
 * only one of those misses the other. When any clinic has a `pms_db`, both
 * keyings are OR'd. Clinics with no `pms_db` stay on `clinic`.
 */
export function getPmsSyncedEntityScopeFilters(
    clinics: ClinicDataSourceRef[],
): QueryInput[] {
    const clinicIds = Array.from(
        new Set(clinics.map(clinic => clinic.id).filter(Boolean)),
    )
    const pmsDbIds = Array.from(
        new Set(
            clinics
                .map(clinic => clinic.pms_db?.id)
                .filter((id): id is string => Boolean(id)),
        ),
    )

    if (pmsDbIds.length === 0) {
        return [constructStringIn('clinic', clinicIds)]
    }

    const clauses = [`pms_db.in.(${pmsDbIds.join(',')})`]
    if (clinicIds.length > 0) {
        clauses.push(`clinic.in.(${clinicIds.join(',')})`)
    }

    return [
        {
            column: POSTGREST_OR_FILTER_COLUMN,
            filterOp: FilterOp.Eq,
            value: { string: clauses.join(',') },
        },
    ]
}

/**
 * Shared-PMS clinicians are duplicated per Chckvet location (same `pms_id`,
 * different `id`). Instinct also has no per-location clinician rows. Match
 * appointments by `clinician.pms_id` in those cases so a filter on the local
 * row still finds visits stored against another location's clinician record.
 */
export function shouldMatchAppointmentsByClinicianPmsId(
    clinic: ClinicDataSource,
): boolean {
    return (
        clinic?.pms === PracticeManagementSystem.Instinct ||
        Boolean(clinic?.pms_db)
    )
}

export function getAppointmentClinicianQueryInput(
    clinic: ClinicDataSource,
    clinicians: AppointmentClinicianRef[],
): QueryInput | undefined {
    if (clinicians.length === 0) return undefined

    if (shouldMatchAppointmentsByClinicianPmsId(clinic)) {
        const pmsIds = getExisting(clinicians.map(clinician => clinician.pms_id))
        if (pmsIds.length > 0) {
            // Instinct clinicians aren't location-specific, so match through
            // the related clinician row. Other shared-PMS appointments store
            // the PMS resource id on the appointment itself.
            const column =
                clinic?.pms === PracticeManagementSystem.Instinct
                    ? 'clinician.pms_id'
                    : 'clinician_pms_id'
            return constructStringIn(column, pmsIds)
        }
    }

    return constructStringIn(
        'clinician',
        getExisting(clinicians.map(clinician => clinician.id)),
    )
}

export function appointmentMatchesClinician(
    appointment: { clinician?: AppointmentClinicianRef | null },
    clinician: AppointmentClinicianRef,
    clinic: ClinicDataSource,
): boolean {
    if (
        shouldMatchAppointmentsByClinicianPmsId(clinic) &&
        appointment.clinician?.pms_id &&
        clinician.pms_id
    ) {
        return appointment.clinician.pms_id === clinician.pms_id
    }

    return appointment.clinician?.id === clinician.id
}

export function constructILike(column: string, query: string): QueryInput {
    return {
        column,
        filterOp: FilterOp.Ilike,
        value: {
            string: `%${query}%`,
        },
    }
}

export function constructStringEq(column: string, value: string): QueryInput {
    return {
        column,
        filterOp: FilterOp.Eq,
        value: {
            string: value,
        },
    }
}

export function constructBooleanEq(column: string, value: boolean): QueryInput {
    return {
        column,
        filterOp: FilterOp.Eq,
        value: {
            boolean: value,
        },
    }
}

export function constructStringIn(
    column: string,
    values: string[],
): QueryInput {
    return {
        column,
        filterOp: FilterOp.In,
        value: {
            stringArray: values,
        },
    }
}

export function constructStringArrayContains(
    column: string,
    values: string[],
): QueryInput {
    return {
        column,
        filterOp: FilterOp.Cs,
        value: {
            string: `{${values.map(value => `"${value}"`).join(',')}}`,
        },
    }
}

export function constructOrderBy(
    column: string,
    isAscending: boolean = false,
): OrderByInput {
    return {
        column,
        isAscending,
    }
}

export function constructIsNull(column: string, not?: boolean): QueryInput {
    return {
        column,
        filterOp: FilterOp.Is,
        value: {
            string: 'null',
        },
        not,
    }
}
